import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, FileText, Eye, ChevronLeft, Loader2 } from 'lucide-react';
import { API, errorText } from '@/utils/api';
import { ReportCover, OrderDispatchComposition } from '@/components/ProductMixAccountReport';

// Compute current FY string ("2026-27") from the user's local date.
const currentFY = () => {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() + 1 >= 4 ? y : y - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
};

// Build a list of selectable FYs: current + 4 prior.
const fyOptions = (count = 5) => {
  const cur = currentFY();
  const startYear = parseInt(cur.slice(0, 4), 10);
  const out = [];
  for (let i = 0; i < count; i++) {
    const sy = startYear - i;
    out.push(`${sy}-${String((sy + 1) % 100).padStart(2, '0')}`);
  }
  return out;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
// Fabric-wise summary (Shirting / Suiting / Ethnic / Other): ordered vs
// dispatched vs pending per fabric, and dispatched pieces / metres / value —
// the same rows as the PDF and Excel. Pending % shaded red at 50%+, amber at 25%+.
function FabricSummary({ rows, showOrders, showDispatches }) {
  const list = (rows || []).filter(r => r.order_lines || r.dispatch_lines);
  if (!list.length) return null;
  const n = (v) => Number(v || 0).toLocaleString('en-IN');
  const tone = (p) => (p == null ? 'text-slate-400' : p >= 50 ? 'bg-rose-50 text-rose-800 font-semibold' : p >= 25 ? 'bg-amber-50 text-amber-800 font-semibold' : 'text-emerald-700');
  return (
    <div className="border rounded-md overflow-x-auto">
      <div className="px-2.5 pt-2 text-[10px] uppercase tracking-wide font-semibold text-slate-600">Fabric-wise summary</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
            <th className="text-left p-1.5">Fabric</th>
            {showOrders && (<>
              <th className="text-right p-1.5">Ordered</th>
              <th className="text-right p-1.5">Dispatched</th>
              <th className="text-right p-1.5">Pending</th>
              <th className="text-right p-1.5">Pending %</th>
            </>)}
            {showDispatches && (<>
              <th className="text-right p-1.5">Pieces</th>
              <th className="text-right p-1.5">Metres</th>
              <th className="text-right p-1.5">Net value</th>
              <th className="text-right p-1.5">% of pieces</th>
            </>)}
          </tr>
        </thead>
        <tbody>
          {list.map(r => (
            <tr key={r.fabric} className="border-t">
              <td className="p-1.5 font-medium">{r.fabric}</td>
              {showOrders && (<>
                <td className="p-1.5 text-right tabular-nums">{n(r.units_ordered)}</td>
                <td className="p-1.5 text-right tabular-nums">{n(r.units_dispatched)}</td>
                <td className="p-1.5 text-right tabular-nums font-semibold">{n(r.units_pending)}</td>
                <td className={`p-1.5 text-right tabular-nums ${tone(r.pending_pct)}`}>{r.pending_pct == null ? '—' : `${r.pending_pct}%`}</td>
              </>)}
              {showDispatches && (<>
                <td className="p-1.5 text-right tabular-nums">{n(r.pieces)}</td>
                <td className="p-1.5 text-right tabular-nums">{Number(r.metres || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}</td>
                <td className="p-1.5 text-right tabular-nums">₹{n(Math.round(r.net_value || 0))}</td>
                <td className="p-1.5 text-right tabular-nums text-slate-500">{r.pct_pieces == null ? '—' : `${r.pct_pieces}%`}</td>
              </>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (ym) => {
  try { return new Date(ym + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); }
  catch { return ym; }
};
// Last 24 months, newest first — the chips for "Pick months".
const recentMonths = () => Array.from({ length: 24 }, (_, i) => shiftMonth(thisMonth(), -i));

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n, d = 0) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: d, minimumFractionDigits: d });
const VIEW_ROW_LIMIT = 500;
const dmy = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

/**
 * Customer Report dialog — pick a period (day / month / a range of months /
 * hand-picked months / FY), pick what to include (orders / dispatches), then
 * VIEW it on screen or download PDF / Excel to share. The on-screen view is
 * built from the same rows as the files (format=json). Used from
 * both the Customers profile sheet and the Orders & Dispatch customer
 * subheader.
 *
 * Props:
 *   open, onOpenChange — standard dialog props
 *   customer: { customer_id?, customer_code?, name? } — REQUIRED (one of id/code)
 *   defaultPeriod: 'day' | 'month' | 'fy'  (default 'day')
 *   defaultPeriodValue: string (matching period type)
 *   defaultDivision: string | undefined
 */
export default function CustomerReportDialog({
  open,
  onOpenChange,
  customer,
  defaultPeriod = 'day',
  defaultPeriodValue,
  defaultDivision,
}) {
  // Credit-note-inclusive reports are customer-facing — admin/accounts only
  // (the backend enforces the same). Pages using this dialog all render
  // inside the Layout outlet, so the user is available from context.
  const outletCtx = useOutletContext() || {};
  const canIncludeCN = outletCtx.user?.role === 'admin' || outletCtx.user?.role === 'accounts';

  const [period, setPeriod] = useState(defaultPeriod);
  const [dayValue, setDayValue] = useState(defaultPeriod === 'day' && defaultPeriodValue ? defaultPeriodValue : todayISO());
  const [monthValue, setMonthValue] = useState(defaultPeriod === 'month' && defaultPeriodValue ? defaultPeriodValue : thisMonth());
  const [fyValue, setFyValue] = useState(defaultPeriod === 'fy' && defaultPeriodValue ? defaultPeriodValue : currentFY());
  const [includeOrders, setIncludeOrders] = useState(true);
  const [includeDispatches, setIncludeDispatches] = useState(true);
  const [includeCreditNotes, setIncludeCreditNotes] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'excel' | null
  // Several months together: a contiguous range, or any hand-picked set.
  const [rangeFrom, setRangeFrom] = useState(shiftMonth(thisMonth(), -1));
  const [rangeTo, setRangeTo] = useState(thisMonth());
  const [pickedMonths, setPickedMonths] = useState([shiftMonth(thisMonth(), -1), thisMonth()]);
  // On-screen view
  const [view, setView] = useState(null);
  const [viewing, setViewing] = useState(false);
  const [viewTab, setViewTab] = useState('dispatches');
  const [viewSearch, setViewSearch] = useState('');
  const [orderFill, setOrderFill] = useState('all');   // all | pending | partial | fulfilled
  const [dispCover, setDispCover] = useState('all');   // all | none | order | bale_auth | certified
  // 'full' = orders/dispatches report · 'pending' = open order lines only,
  // with order dates and a short summary, for follow-up with the principal.
  const [reportType, setReportType] = useState('full');
  // "For" filter — who the wholesaler placed the orders for. 'all' = whole
  // account; 'own' / 'untagged' are sentinels; anything else is a retailer_id.
  // Radix Select cannot hold an empty value, hence the sentinels.
  const [endCustomer, setEndCustomer] = useState('all');
  const [retailers, setRetailers] = useState([]);
  const [isWholesaler, setIsWholesaler] = useState(false);
  // "Store" filter — which ship-to location under the SAP code. 'all' = whole
  // account; 'head' / 'none' are sentinels; anything else is a ship_to_code.
  const [store, setStore] = useState('all');
  const [stores, setStores] = useState([]);

  // Reset internal state whenever the dialog reopens with fresh defaults
  useEffect(() => {
    if (!open) return;
    setEndCustomer('all');
    setRetailers([]);
    setIsWholesaler(false);
    setStore('all');
    setStores([]);
    setPeriod(defaultPeriod);
    if (defaultPeriod === 'day') setDayValue(defaultPeriodValue || todayISO());
    if (defaultPeriod === 'month') setMonthValue(defaultPeriodValue || thisMonth());
    if (defaultPeriod === 'fy') setFyValue(defaultPeriodValue || currentFY());
    if (defaultPeriod === 'range') { setRangeFrom(shiftMonth(thisMonth(), -1)); setRangeTo(thisMonth()); }
    if (defaultPeriod === 'months') setPickedMonths([shiftMonth(thisMonth(), -1), thisMonth()]);
    setIncludeOrders(true);
    setIncludeDispatches(true);
    setIncludeCreditNotes(false);
    setView(null);
    setViewSearch('');
    setViewTab('products');
    setOrderFill('all');
    setDispCover('all');
    setReportType('full');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Retailers attached to this wholesaler's profile drive the "For" filter.
  // The endpoint takes a customer_id OR a customer_code (Orders & Dispatch
  // only passes the code). A quiet failure is fine: the filter simply hides.
  useEffect(() => {
    if (!open) return;
    const ref = customer?.customer_id || customer?.customer_code;
    if (!ref) return;
    let cancelled = false;   // the dialog may close before the reply lands
    const params = {};
    if (defaultDivision && defaultDivision !== 'all') params.division = defaultDivision;
    axios.get(`${API}/customers/${encodeURIComponent(ref)}/retailers`, { params, withCredentials: true })
      .then(res => {
        if (cancelled) return;
        setRetailers(Array.isArray(res.data?.retailers) ? res.data.retailers : []);
        setIsWholesaler(!!res.data?.is_wholesaler);
      })
      .catch(() => { /* not a wholesaler, or no access — keep the filter hidden */ });
    // Stores (ship-to locations) the same way; the filter hides when none.
    axios.get(`${API}/customers/${encodeURIComponent(ref)}/stores`, { params, withCredentials: true })
      .then(res => { if (!cancelled) setStores(Array.isArray(res.data?.stores) ? res.data.stores : []); })
      .catch(() => { /* no stores, or no access — keep the filter hidden */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.customer_id, customer?.customer_code]);

  const activeRetailers = useMemo(() => retailers.filter(r => r.active !== false), [retailers]);
  // Human label for the chosen "Store" value — preview, file name, view header.
  const storeLabel = useMemo(() => {
    if (store === 'all') return '';
    if (store === 'head') return 'SAP code itself';
    if (store === 'none') return 'No store';
    const s = stores.find(x => x.ship_to_code === store);
    return s ? (s.display_name || s.name || s.ship_to_code) : store;
  }, [store, stores]);
  // Human label for the chosen "For" value — used in the preview, the file
  // name and the view header.
  const endCustomerLabel = useMemo(() => {
    if (endCustomer === 'all') return '';
    if (endCustomer === 'own') return 'Own shop';
    if (endCustomer === 'untagged') return 'Not tagged';
    const r = retailers.find(x => x.retailer_id === endCustomer);
    return r ? r.name : '';
  }, [endCustomer, retailers]);

  const periodValue = useMemo(() => {
    if (period === 'day') return dayValue;
    if (period === 'month') return monthValue;
    if (period === 'range') return rangeFrom && rangeTo ? `${rangeFrom}:${rangeTo}` : '';
    if (period === 'months') return [...pickedMonths].sort().join(',');
    return fyValue;
  }, [period, dayValue, monthValue, fyValue, rangeFrom, rangeTo, pickedMonths]);

  const periodLabel = useMemo(() => {
    if (period === 'day' && dayValue) {
      try {
        const d = new Date(dayValue + 'T00:00:00');
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return dayValue; }
    }
    if (period === 'month' && monthValue) {
      try {
        const d = new Date(monthValue + '-01T00:00:00');
        return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      } catch { return monthValue; }
    }
    if (period === 'range' && rangeFrom && rangeTo) {
      const [a, b] = rangeFrom <= rangeTo ? [rangeFrom, rangeTo] : [rangeTo, rangeFrom];
      return a === b ? monthLabel(a) : `${monthLabel(a)} to ${monthLabel(b)}`;
    }
    if (period === 'months') return [...pickedMonths].sort().map(monthLabel).join(', ') || '—';
    if (period === 'fy') return `FY ${fyValue}`;
    return '';
  }, [period, dayValue, monthValue, fyValue, rangeFrom, rangeTo, pickedMonths]);

  const togglePicked = (m) => setPickedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  const isPending = reportType === 'pending';
  const canSubmit = !!periodValue && (isPending || includeOrders || includeDispatches) && (customer?.customer_id || customer?.customer_code);
  // (download buttons stay available in view mode — for sharing what is on screen)

  const buildParams = (format) => {
    const params = {
      period,
      period_value: periodValue,
      include: isPending ? 'orders,dispatches' : [includeOrders ? 'orders' : null, includeDispatches ? 'dispatches' : null].filter(Boolean).join(','),
      include_credit_notes: isPending ? false : includeCreditNotes,
      format,
      report: reportType,
    };
    // Send BOTH when available — the endpoint tries the id first, then falls
    // back to the code, so a stale/missing customer_id can't 404 the report.
    if (customer.customer_id) params.customer_id = customer.customer_id;
    if (customer.customer_code) params.customer_code = customer.customer_code;
    if (defaultDivision && defaultDivision !== 'all') params.division = defaultDivision;
    // "For" applies to the full AND the pending report alike.
    if (endCustomer && endCustomer !== 'all') params.end_customer = endCustomer;
    // "Store" likewise (ship_to_code | 'head' | 'none').
    if (store && store !== 'all') params.store = store;
    return params;
  };

  const handleView = async () => {
    if (!canSubmit) {
      if (!includeOrders && !includeDispatches) toast.error('Pick at least one of Orders or Dispatches');
      else if (period === 'months') toast.error('Pick at least one month');
      return;
    }
    setViewing(true);
    try {
      const res = await axios.get(`${API}/customers/period-report`, { params: buildParams('json'), withCredentials: true });
      setView(res.data);
      setViewSearch('');
      setViewTab(isPending ? 'pending' : (includeDispatches ? 'products' : 'months'));
    } catch (err) {
      toast.error(errorText(err) || `Could not load: ${err.message}`);
    } finally {
      setViewing(false);
    }
  };

  const handleDownload = async (format) => {
    if (!canSubmit) {
      if (!includeOrders && !includeDispatches) toast.error('Pick at least one of Orders or Dispatches');
      return;
    }
    setDownloading(format);
    try {
      const params = buildParams(format);

      const res = await axios.get(`${API}/customers/period-report`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const safeName = (customer.name || customer.customer_code || 'Customer').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40);
      // A filtered report carries the retailer / Own shop label in its name so
      // two downloads for the same period cannot be mistaken for each other.
      const forPart = endCustomerLabel ? `_${endCustomerLabel.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 30)}` : '';
      const storePart = storeLabel ? `_${storeLabel.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 30)}` : '';
      link.setAttribute('download', `${isPending ? 'Pending_Orders' : 'Customer'}_${safeName}${forPart}${storePart}_${String(periodValue).replace(/[^0-9A-Za-z-]+/g, '_')}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
      if (!view) onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err) || `Export failed: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const q = viewSearch.trim().toLowerCase();
  const viewDispatches = useMemo(() => (view?.dispatches || []).filter(r =>
    (dispCover === 'all' || (!r.is_credit_note && r.cover === dispCover)) && (!q ||
    [r.invoice, r.trade_name, r.sort_no, r.po, r.date, r.order_no, r.for_name, r.store_name].some(x => (x || '').toLowerCase().includes(q)))), [view, q, dispCover]);
  const viewOrders = useMemo(() => (view?.orders || []).filter(r =>
    (orderFill === 'all' || r.fill === orderFill) && (!q ||
    [r.order_no, r.trade_name, r.product_code, r.date, r.for_name, r.store_name].some(x => (x || '').toLowerCase().includes(q)))), [view, q, orderFill]);
  // Store columns only when the profile has stores or a store filter is on —
  // the server sets show_store under the same rule, so the column matches
  // the Excel/PDF.
  const showStore = !!(view?.show_store || view?.store_filter?.value || (view?.by_store || []).length > 0);
  // CTG (the invoice category) is GG-only: the server fills it on GG rows alone.
  const showCtg = (view?.dispatches || []).some(r => r.ctg && (r.division || '').toUpperCase().startsWith('GG'));
  const storeCell = (r) => r.store_name
    ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200" title={r.store_code || ''}>{r.store_name}</span>
    : <span className="text-slate-300">—</span>;

  // "For" badge — who the wholesaler placed the line for. Retailers are sky,
  // Own shop amber, Split / Shared violet (shared = outline, it is a derived
  // dispatch-side state), anything untagged or without an order copy is muted.
  const forTone = (kind) => {
    if (kind === 'retailer') return 'bg-sky-100 text-sky-800';
    if (kind === 'own') return 'bg-amber-100 text-amber-800';
    if (kind === 'split') return 'bg-violet-100 text-violet-800';
    if (kind === 'shared') return 'bg-white border border-violet-300 text-violet-700';
    return 'bg-slate-100 text-slate-500';
  };
  const forBadge = (kind, name, split) => {
    if (!name && !kind) return null;   // DTR / TAS lines: "For" doesn't apply
    const title = kind === 'split' && Array.isArray(split) && split.length
      ? split.map(s => `${s.name || (s.kind === 'own' ? 'Own shop' : 'Retailer')}: ${num(s.units)}`).join(' · ')
      : undefined;
    return (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${forTone(kind)}`} title={title}>
        {name || 'Not tagged'}
      </span>
    );
  };

  const renderPendingView = () => {
    const s = view.summary || {};
    const rows = (view.pending || []).filter(r => !q ||
      [r.order_no, r.trade_name, r.product_code, r.date, r.category, r.for_name, r.store_name,
       ...((r.for_split || []).map(sp => sp.name))].some(x => (x || '').toLowerCase().includes(q)));
    const catTone = { Shirting: 'bg-sky-100 text-sky-800', Suiting: 'bg-violet-100 text-violet-800', Ethnic: 'bg-amber-100 text-amber-800' };
    const th = 'text-left py-1.5 px-2 font-medium text-slate-500 whitespace-nowrap';
    const thr = 'text-right py-1.5 px-2 font-medium text-slate-500 whitespace-nowrap';
    const td = 'py-1 px-2 whitespace-nowrap';
    const tdr = 'py-1 px-2 text-right whitespace-nowrap tabular-nums';
    const pct = (x) => (x == null ? '' : ` (${num(x, 1)}%)`);
    return (
      <div className="space-y-3">
        <button type="button" onClick={() => setView(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
          <ChevronLeft size={12} /> Change period or report
        </button>
        <div className="text-xs text-slate-600">
          <span className="font-semibold text-slate-800">Pending orders</span>{' · '}
          <span className="font-semibold text-slate-800">{view.customer?.name}</span>
          {view.customer?.location && <span className="text-sky-700 font-medium">{' · '}{view.customer.location}</span>}
          {' · '}{(view.customer?.codes || []).join(', ')}{' · '}{view.customer?.division}{' · '}{view.customer?.region}
          {' · orders placed in '}<span className="font-semibold text-indigo-700">{view.period?.label}</span>
          {view.end_customer_filter?.value && (
            <>{' · For: '}<span className="font-semibold text-sky-800">{view.end_customer_filter.label}</span></>
          )}
          {view.store_filter?.value && (
            <>{' · Store: '}<span className="font-semibold text-amber-800">{view.store_filter.label}</span></>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ['Orders placed', `${num(s.orders_placed)} orders · ${num(s.order_lines)} lines`],
            ['Units ordered', num(s.units_ordered)],
            ['Dispatched', `${num(s.units_dispatched)}${pct(s.pct_fulfilled)}`],
            ['Pending', `${num(s.units_pending)}${pct(s.pct_pending)}`],
            ['Still open', `${num(s.pending_orders)} orders · ${num(s.pending_lines)} lines`],
            ['Oldest pending', s.oldest_pending_date ? `${dmy(s.oldest_pending_date)} (${num(s.oldest_pending_days)} d)` : '—'],
            ['Dispatched in period', `${num(s.dispatch_invoices)} inv · ${num(s.dispatch_metres, 0)} m`],
            ['Dispatch value (net)', inr(s.dispatch_net_value)],
          ].map(([k, v]) => (
            <div key={k} className={`border rounded-md px-2.5 py-1.5 ${k === 'Pending' ? 'bg-rose-50 border-rose-200' : 'bg-slate-50'}`}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
              <div className={`text-sm font-bold ${k === 'Pending' ? 'text-rose-700' : 'text-slate-900'}`}>{v}</div>
            </div>
          ))}
        </div>
        {(view.by_category || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Pending by category</span>
            {view.by_category.map(b => (
              <button key={b.category} type="button" onClick={() => setViewSearch(viewSearch === b.category ? '' : b.category)}
                className={`px-2 py-0.5 rounded font-semibold ${catTone[b.category] || 'bg-slate-100 text-slate-700'} ${viewSearch === b.category ? 'ring-2 ring-indigo-400' : ''}`}
                title="Show only this category">
                {b.category}: {num(b.units_pending)} units · {num(b.lines)} lines
              </button>
            ))}
          </div>
        )}
        {(view.by_end_customer || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Pending by retailer</span>
            {view.by_end_customer.map(b => (
              <button key={`${b.kind}:${b.id || ''}`} type="button" onClick={() => setViewSearch(viewSearch === b.name ? '' : b.name)}
                className={`px-2 py-0.5 rounded font-semibold ${forTone(b.kind)} ${viewSearch === b.name ? 'ring-2 ring-indigo-400' : ''}`}
                title="Show only the lines placed for this retailer">
                {b.name}: {num(b.units_pending)} units · {num(b.lines)} lines
              </button>
            ))}
          </div>
        )}
        {(view.by_store || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Pending by store</span>
            {view.by_store.map(b => (
              <button key={`store:${b.ship_to_code || ''}`} type="button" onClick={() => setViewSearch(viewSearch === b.name ? '' : b.name)}
                className={`px-2 py-0.5 rounded font-semibold ${b.ship_to_code ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'} ${viewSearch === b.name ? 'ring-2 ring-indigo-400' : ''}`}
                title="Show only the lines for this store">
                {b.name}: {num(b.units_pending)} units · {num(b.lines ?? b.order_lines)} lines
              </button>
            ))}
          </div>
        )}
        <FabricSummary rows={view.by_fabric} showOrders showDispatches={false} />
        <Input value={viewSearch} onChange={(e) => setViewSearch(e.target.value)} placeholder="Search order / for / store / category / trade / sort / date…"
          className="h-7 text-xs w-full sm:w-64" />
        {(view.divisions || []).length === 0 && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
            No pending orders — every order placed in this period has been dispatched in full.
          </div>
        )}
        <div className="border rounded-md overflow-auto max-h-[46vh]">
          {(view.divisions || []).map(d => {
            const dr = rows.filter(r => (r.division || '-') === d.division);
            if (!dr.length) return null;
            return (
              <table key={d.division} className="w-full text-xs mb-2">
                <thead className="sticky top-0 bg-white border-b">
                  <tr><th colSpan={showStore ? 13 : 12} className="text-left py-1.5 px-2 text-slate-800 font-semibold bg-slate-50">
                    {d.division} — {num(d.orders)} open orders · {num(d.lines)} lines · <span className="text-rose-700">{num(d.units_pending)} units pending</span>
                  </th></tr>
                  <tr>
                    <th className={th}>Order date</th><th className={th}>Order no</th><th className={th}>For</th>{showStore && <th className={th}>Store</th>}<th className={thr}>Days</th><th className={th}>Category</th><th className={th}>Trade / quality</th>
                    <th className={th}>Sort no</th><th className={thr}>Ordered</th><th className={thr}>Dispatched</th><th className={thr}>Pending</th>
                    <th className={thr}>Ex-mill ₹/m</th><th className={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dr.slice(0, VIEW_ROW_LIMIT).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className={td}>{dmy(r.date)}</td><td className={td}>{r.order_no}</td>
                      <td className={td}>{forBadge(r.for_kind, r.for_name, r.for_split)}</td>
                      {showStore && <td className={td}>{storeCell(r)}</td>}
                      <td className={`${tdr} ${r.days_pending > 60 ? 'text-rose-700 font-semibold' : ''}`}>{r.days_pending ?? '—'}</td>
                      <td className={td}>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${catTone[r.category] || 'bg-slate-100 text-slate-600'}`}>{r.category || 'Unclear'}</span>
                      </td>
                      <td className={td}>{r.trade_name}</td><td className={td}>{r.product_code}</td>
                      <td className={tdr}>{num(r.units_ordered)}</td><td className={tdr}>{num(r.units_dispatched)}</td>
                      <td className={`${tdr} text-rose-700 font-semibold`}>{num(r.units_pending)}</td>
                      <td className={tdr}>{r.ex_mill_price ? num(r.ex_mill_price, 2) : '—'}</td>
                      <td className={td}>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.fill === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                          {r.fill === 'partial' ? 'part-filled' : 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500">
          Units are pieces as on the order copies · dispatched = shipped to date against these orders · a return does not reopen an order ·
          days = order date to today · dispatch value excludes GST · no value is shown for pending units (piece lengths vary).
        </p>
      </div>
    );
  };

  const renderView = () => {
    if (view.report === 'pending') return renderPendingView();
    const t = view.totals || {};
    // Summaries first; the line-by-line registers sit last (the analysis
    // matters more than the raw lines — same order as the PDF).
    const tabs = [
      ...(view.includes.includes('dispatches') ? [['products', `Products (${(view.products || []).length})`]] : []),
      ['months', `Month-wise (${(view.months || []).length})`],
      ...((view.by_end_customer || []).length > 0 ? [['by_retailer', `By retailer (${view.by_end_customer.length})`]] : []),
      ...((view.by_store || []).length > 0 ? [['by_store', `By store (${view.by_store.length})`]] : []),
      ...(view.includes.includes('orders') ? [['orders', `Order lines (${t.order_lines || 0})`]] : []),
      ...(view.includes.includes('dispatches') ? [['dispatches', `Dispatch lines (${t.dispatch_lines || 0})`]] : []),
    ];
    const th = 'text-left py-1.5 px-2 font-medium text-slate-500 whitespace-nowrap';
    const thr = 'text-right py-1.5 px-2 font-medium text-slate-500 whitespace-nowrap';
    const td = 'py-1 px-2 whitespace-nowrap';
    const tdr = 'py-1 px-2 text-right whitespace-nowrap tabular-nums';
    return (
      <div className="space-y-3">
        <button type="button" onClick={() => setView(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
          <ChevronLeft size={12} /> Change period or contents
        </button>
        {/* Report-kit cover: the same headline figures, fabric cards and highlights as the PDF. */}
        <ReportCover cover={view.cover} customer={view.customer} period={view.period?.label}
          extras={[
            view.end_customer_filter?.value && `For: ${view.end_customer_filter.label}`,
            view.store_filter?.value && `Store: ${view.store_filter.label}`,
            view.includes.includes('dispatches') && (view.credit_notes_included ? 'net of goods returns' : 'returns excluded'),
          ]} />
        <OrderDispatchComposition comp={view.composition} />
        {view.includes.includes('dispatches') && view.order_coverage && (() => {
          const c = view.order_coverage;
          const seg = [
            ['order', 'With order copy', 'bg-emerald-500', 'text-emerald-700'],
            ['bale_auth', 'Bale authorisation', 'bg-sky-500', 'text-sky-700'],
            ['certified', 'Certified direct', 'bg-slate-400', 'text-slate-600'],
            ['none', 'No order copy', 'bg-rose-500', 'text-rose-700'],
          ];
          return (
            <div className="border rounded-md p-2.5 bg-slate-50/60">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-600">Dispatched in this period vs order copies</span>
                {seg.filter(([k]) => (c[k]?.lines || 0) > 0 || k === 'none').map(([k, label, , tone]) => (
                  <button key={k} type="button" onClick={() => { setViewTab('dispatches'); setDispCover(k); }}
                    className={`${tone} hover:underline`} title="Show these dispatch lines">
                    {label}: <span className="font-bold">{num(c[k]?.metres, 0)} m</span>
                    {c[k]?.pct_metres != null && ` (${num(c[k].pct_metres, 1)}%)`}
                    {' · '}{num(c[k]?.invoices)} inv · {inr(c[k]?.net_value)}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-200 overflow-hidden flex">
                {seg.map(([k, , bar]) => (c[k]?.pct_metres ? <div key={k} className={`h-full ${bar}`} style={{ width: `${c[k].pct_metres}%` }} /> : null))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                By metres, returns left out. "No order copy" = shipped with no uploaded order it could be matched to — chase the copy, or certify it as a direct dispatch. Click a figure to list those lines.
              </div>
            </div>
          );
        })()}
        {view.includes.includes('orders') && view.order_fulfilment && (() => {
          const f = view.order_fulfilment;
          const pctF = f.pct_fulfilled ?? 0;
          return (
            <div className="border rounded-md p-2.5 bg-amber-50/40 border-amber-200">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-slate-700">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-800">Orders placed in this period</span>
                <span><span className="font-bold text-slate-900">{num(f.orders)}</span> orders · {num(f.lines)} lines</span>
                <span><span className="font-bold text-slate-900">{num(f.units_ordered)}</span> units ordered</span>
                <span className="text-emerald-700"><span className="font-bold">{num(f.units_dispatched)}</span> dispatched{f.pct_fulfilled != null && ` (${num(f.pct_fulfilled, 1)}%)`}</span>
                <span className="text-rose-700"><span className="font-bold">{num(f.units_pending)}</span> pending{f.pct_pending != null && ` (${num(f.pct_pending, 1)}%)`}</span>
                <span className="text-slate-500">{num(f.orders_open)} orders still open</span>
              </div>
              {f.units_ordered > 0 && (
                <div className="mt-1.5 h-2 w-full rounded-full bg-rose-200 overflow-hidden" title={`${num(pctF, 1)}% of ordered units dispatched`}>
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pctF)}%` }} />
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                Units are pieces, as on the order copies. Dispatched = dispatched to date against these orders (capped at the ordered qty per line), so a dispatch after the period still counts.
              </div>
            </div>
          );
        })()}
        <FabricSummary rows={view.by_fabric} showOrders={view.includes.includes('orders')} showDispatches={view.includes.includes('dispatches')} />
        {(view.by_ctg || []).length > 0 && (
          // GG: dispatches by the invoice's CTG (category: F, SH1, TLB, TLF…).
          <div className="border rounded-md overflow-auto">
            <div className="px-2.5 pt-2 text-[10px] uppercase tracking-wide font-semibold text-slate-600">Category (CTG)-wise summary</div>
            <table className="w-full text-xs">
              <thead><tr className="border-b">
                {['CTG', 'Invoices', 'Pieces', 'Metres', 'Net value', '% of metres', 'Avg ₹/m'].map((h, i) => (
                  <th key={h} className={i ? thr : th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {view.by_ctg.map(r => (
                  <tr key={r.ctg} className="border-t border-slate-100">
                    <td className={`${td} font-semibold`}>{r.ctg}</td>
                    <td className={tdr}>{num(r.invoices)}</td><td className={tdr}>{num(r.pieces)}</td>
                    <td className={tdr}>{num(r.metres, 1)}</td><td className={tdr}>{inr(r.net_value)}</td>
                    <td className={tdr}>{r.pct_metres != null ? `${num(r.pct_metres, 1)}%` : '—'}</td>
                    <td className={tdr}>{r.avg_rate != null ? `₹${num(r.avg_rate)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setViewTab(k)}
              className={`text-xs px-2.5 py-1 rounded-md ${viewTab === k ? 'bg-indigo-600 text-white font-semibold' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {label}
            </button>
          ))}
          {viewTab === 'dispatches' && view.order_coverage && (
            <div className="flex flex-wrap items-center gap-1 ml-2">
              {[['all', 'All'], ['none', `No order copy (${view.order_coverage.none?.lines ?? 0})`], ['order', `With order (${view.order_coverage.order?.lines ?? 0})`],
                ...(view.order_coverage.bale_auth?.lines ? [['bale_auth', `Bale auth (${view.order_coverage.bale_auth.lines})`]] : []),
                ...(view.order_coverage.certified?.lines ? [['certified', `Certified (${view.order_coverage.certified.lines})`]] : [])].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setDispCover(k)}
                  className={`text-[11px] px-2 py-0.5 rounded border ${dispCover === k ? 'bg-slate-700 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
            </div>
          )}
          {viewTab === 'orders' && (
            <div className="flex items-center gap-1 ml-2">
              {[['all', 'All'], ['pending', `Pending (${view.order_fulfilment?.lines_pending ?? 0})`], ['partial', `Part-filled (${view.order_fulfilment?.lines_partial ?? 0})`], ['fulfilled', `Fulfilled (${view.order_fulfilment?.lines_fulfilled ?? 0})`]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setOrderFill(k)}
                  className={`text-[11px] px-2 py-0.5 rounded border ${orderFill === k ? 'bg-slate-700 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
            </div>
          )}
          {(viewTab === 'dispatches' || viewTab === 'orders') && (
            <Input value={viewSearch} onChange={(e) => setViewSearch(e.target.value)} placeholder="Search invoice / order / trade / sort…"
              className="h-7 text-xs ml-auto w-full sm:w-64" />
          )}
        </div>
        <div className="border rounded-md overflow-auto max-h-[46vh]">
          {viewTab === 'dispatches' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b"><tr>
                <th className={th}>Date</th><th className={th}>Invoice</th><th className={th}>Trade</th>{showCtg && <th className={th}>CTG</th>}<th className={th}>Sort no</th>
                <th className={thr}>Metres</th><th className={thr}>Pieces</th><th className={thr}>Net ₹/m</th><th className={thr}>Net value</th><th className={th}>Order</th><th className={th}>For</th>{showStore && <th className={th}>Store</th>}<th className={th}>Division</th>
              </tr></thead>
              <tbody>
                {viewDispatches.slice(0, VIEW_ROW_LIMIT).map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${r.is_credit_note ? 'bg-rose-50 text-rose-800' : ''}`}>
                    <td className={td}>{r.date}</td>
                    <td className={td}>{r.invoice}{r.is_credit_note && <span className="ml-1 text-[9px] font-semibold uppercase">return</span>}</td>
                    <td className={td}>{r.trade_name}</td>{showCtg && <td className={td}>{r.ctg || '—'}</td>}<td className={td}>{r.sort_no}</td>
                    <td className={tdr}>{num(r.metres, 1)}</td><td className={tdr}>{num(r.pieces)}</td>
                    <td className={tdr}>{r.net_rate ? num(r.net_rate, 2) : '—'}</td><td className={tdr}>{inr(r.net_value)}</td>
                    <td className={td}>
                      {r.is_credit_note ? '' : r.cover === 'order' ? <span className="text-emerald-700">{r.order_no || 'matched'}</span>
                        : r.cover === 'bale_auth' ? <span className="text-sky-700">bale auth</span>
                        : r.cover === 'certified' ? <span className="text-slate-500">certified</span>
                        : <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold">no order copy</span>}
                    </td>
                    <td className={td}>{r.is_credit_note ? '' : forBadge(r.for_kind, r.for_name)}</td>
                    {showStore && <td className={td}>{storeCell(r)}</td>}
                    <td className={`${td} text-slate-400`}>{r.division}</td>
                  </tr>
                ))}
                {viewDispatches.length === 0 && <tr><td colSpan={showStore ? 12 : 11} className="py-6 text-center text-slate-400">No dispatch lines{q || dispCover !== 'all' ? ' match the filter' : ' in this period'}.</td></tr>}
              </tbody>
            </table>
          )}
          {viewTab === 'orders' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b"><tr>
                <th className={th}>Date</th><th className={th}>Order no</th><th className={th}>For</th>{showStore && <th className={th}>Store</th>}<th className={th}>Trade</th><th className={th}>Product code</th>
                <th className={thr}>Ordered</th><th className={thr}>Dispatched</th><th className={thr}>Pending</th><th className={th}>Status</th><th className={thr}>Ex-mill ₹/m</th><th className={th}>Division</th>
              </tr></thead>
              <tbody>
                {viewOrders.slice(0, VIEW_ROW_LIMIT).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className={td}>{r.date}</td><td className={td}>{r.order_no}</td>
                    <td className={td}>{forBadge(r.for_kind, r.for_name, r.for_split)}</td>
                    {showStore && <td className={td}>{storeCell(r)}</td>}
                    <td className={td}>{r.trade_name}</td><td className={td}>{r.product_code}</td>
                    <td className={tdr}>{num(r.units_ordered)}</td><td className={tdr}>{num(r.units_dispatched)}</td>
                    <td className={`${tdr} ${r.units_pending > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{num(r.units_pending)}</td>
                    <td className={td}>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.fill === 'fulfilled' ? 'bg-emerald-100 text-emerald-800' : r.fill === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                        {r.fill === 'fulfilled' ? 'fulfilled' : r.fill === 'partial' ? 'part-filled' : 'pending'}
                      </span>
                    </td><td className={tdr}>{r.ex_mill_price ? num(r.ex_mill_price, 2) : '—'}</td>
                    <td className={`${td} text-slate-400`}>{r.division}</td>
                  </tr>
                ))}
                {viewOrders.length === 0 && <tr><td colSpan={showStore ? 12 : 11} className="py-6 text-center text-slate-400">No order lines{q || orderFill !== 'all' ? ' match the filter' : ' in this period'}.</td></tr>}
              </tbody>
            </table>
          )}
          {viewTab === 'products' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b"><tr>
                <th className={th}>#</th><th className={th}>Trade</th><th className={thr}>Metres</th><th className={thr}>Pieces</th><th className={thr}>Net value</th><th className={thr}>% of value</th>
              </tr></thead>
              <tbody>
                {(view.products || []).map((r, i) => (
                  <tr key={r.trade_name} className="border-t border-slate-100">
                    <td className={`${td} text-slate-400`}>{i + 1}</td><td className={td}>{r.trade_name}</td>
                    <td className={tdr}>{num(r.metres, 1)}</td><td className={tdr}>{num(r.pieces)}</td>
                    <td className={tdr}>{inr(r.net_value)}</td><td className={tdr}>{num(r.pct_value, 1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {viewTab === 'months' && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white border-b"><tr>
                <th className={th}>Month</th><th className={thr}>Metres</th><th className={thr}>Pieces</th><th className={thr}>Net value</th><th className={thr}>Dispatch lines</th><th className={thr}>Units ordered</th>
              </tr></thead>
              <tbody>
                {(view.months || []).map(r => (
                  <tr key={r.month} className="border-t border-slate-100">
                    <td className={td}>{monthLabel(r.month)}</td><td className={tdr}>{num(r.metres, 1)}</td><td className={tdr}>{num(r.pieces)}</td>
                    <td className={tdr}>{inr(r.net_value)}</td><td className={tdr}>{num(r.lines)}</td><td className={tdr}>{num(r.units_ordered || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {viewTab === 'by_retailer' && (
            <div>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b"><tr>
                  <th className={th}>For</th><th className={thr}>Order lines</th><th className={thr}>Units ordered</th><th className={thr}>Dispatched</th><th className={thr}>Pending</th>
                  <th className={thr}>Dispatch lines</th><th className={thr}>Invoices</th><th className={thr}>Metres</th><th className={thr}>Net value</th><th className={thr}>% units</th><th className={thr}>% value</th>
                </tr></thead>
                <tbody>
                  {/* Server order is deliberate: Own shop, retailers, Not tagged, then the dispatch-only rows */}
                  {(view.by_end_customer || []).map(r => (
                    <tr key={`${r.kind}:${r.id || ''}`} className="border-t border-slate-100">
                      <td className={td}>{forBadge(r.kind, r.name)}</td>
                      <td className={tdr}>{num(r.order_lines)}</td><td className={tdr}>{num(r.units_ordered)}</td><td className={tdr}>{num(r.units_dispatched)}</td>
                      <td className={`${tdr} ${r.units_pending > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{num(r.units_pending)}</td>
                      <td className={tdr}>{num(r.dispatch_lines)}</td><td className={tdr}>{num(r.invoices)}</td><td className={tdr}>{num(r.metres, 1)}</td>
                      <td className={tdr}>{inr(r.net_value)}</td>
                      <td className={tdr}>{r.pct_units != null ? `${num(r.pct_units, 1)}%` : '—'}</td>
                      <td className={tdr}>{r.pct_value != null ? `${num(r.pct_value, 1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-500 px-2 py-1.5 border-t border-slate-100">
                Dispatch figures follow order matching; shared invoice lines and dispatches without an order copy are listed separately. Returns are not attributed.
              </p>
            </div>
          )}
          {viewTab === 'by_store' && (
            <div>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b"><tr>
                  <th className={th}>Store</th><th className={thr}>Order lines</th><th className={thr}>Units ordered</th><th className={thr}>Dispatched</th><th className={thr}>Pending</th>
                  <th className={thr}>Dispatch lines</th><th className={thr}>Invoices</th><th className={thr}>Metres</th><th className={thr}>Pieces</th><th className={thr}>Net value</th><th className={thr}>% units</th><th className={thr}>% value</th>
                </tr></thead>
                <tbody>
                  {/* Server order is deliberate: Head office first, stores by name, then "No store" rows */}
                  {(view.by_store || []).map(r => (
                    <tr key={`store:${r.ship_to_code || ''}`} className="border-t border-slate-100">
                      <td className={td}>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.ship_to_code ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`} title={r.ship_to_code || ''}>
                          {r.name || (r.ship_to_code ? r.ship_to_code : 'No store')}
                        </span>
                        {r.is_head && <span className="ml-1 text-[10px] text-slate-400">head</span>}
                      </td>
                      <td className={tdr}>{num(r.order_lines)}</td><td className={tdr}>{num(r.units_ordered)}</td><td className={tdr}>{num(r.units_dispatched)}</td>
                      <td className={`${tdr} ${r.units_pending > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{num(r.units_pending)}</td>
                      <td className={tdr}>{num(r.dispatch_lines)}</td><td className={tdr}>{num(r.invoices)}</td><td className={tdr}>{num(r.metres, 1)}</td><td className={tdr}>{num(r.pieces)}</td>
                      <td className={tdr}>{inr(r.net_value)}</td>
                      <td className={tdr}>{r.pct_units != null ? `${num(r.pct_units, 1)}%` : '—'}</td>
                      <td className={tdr}>{r.pct_value != null ? `${num(r.pct_value, 1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-500 px-2 py-1.5 border-t border-slate-100">
                Store = the ship-to location on the file. Orders and dispatches are read directly from their own ship-to; billing stays on the SAP code. Returns are not attributed.
              </p>
            </div>
          )}
        </div>
        <p className="text-[11px] text-slate-500">
          Values exclude GST (net value after discounts, before tax) · rates are per metre · order units are pieces.
        </p>
        {((viewTab === 'dispatches' && viewDispatches.length > VIEW_ROW_LIMIT) || (viewTab === 'orders' && viewOrders.length > VIEW_ROW_LIMIT)) && (
          <p className="text-[11px] text-slate-500">
            Showing the latest {VIEW_ROW_LIMIT} of {num(viewTab === 'dispatches' ? viewDispatches.length : viewOrders.length)} lines — search to narrow, or download Excel for all. Totals above cover every line.
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped at the viewport: header + buttons stay put, the middle scrolls
          (the View is taller than most screens and was pushing both off-screen). */}
      <DialogContent className={`${view ? 'max-w-[1500px] w-[96vw]' : 'max-w-md'} max-h-[94vh] flex flex-col gap-3`}>
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText size={16} className="text-indigo-600" />
            Customer Report
          </DialogTitle>
          <DialogDescription>
            {customer?.name ? <span><span className="font-medium">{customer.name}</span> ({customer.customer_code || customer.customer_id})</span> : 'Pick a period, then view it here or download to share.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
        {view ? renderView() : (
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Report</Label>
            <RadioGroup value={reportType} onValueChange={setReportType} className="grid grid-cols-1 gap-2 mt-1">
              {[['full', 'Full report', 'Orders and/or dispatches for the period'],
                ['pending', 'Pending orders only', 'Open order lines with their order dates and a short summary — to send to the company for follow-up']].map(([v, l, hint]) => (
                <Label key={v} className={`flex items-start gap-2 p-2 border rounded cursor-pointer text-xs ${reportType === v ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value={v} className="mt-0.5" />
                  <span><span className="font-medium text-slate-800">{l}</span><span className="block text-[11px] text-slate-500 font-normal">{hint}</span></span>
                </Label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-xs">{isPending ? 'Orders placed in' : 'Period'}</Label>
            <RadioGroup value={period} onValueChange={setPeriod} className="grid grid-cols-3 gap-2 mt-1">
              {[['day', 'Day'], ['month', 'Month'], ['range', 'Month range'], ['months', 'Pick months'], ['fy', 'FY']].map(([v, l]) => (
                <Label key={v} className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${period === v ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                  <RadioGroupItem value={v} /> {l}
                </Label>
              ))}
            </RadioGroup>
          </div>
          {period === 'day' && (
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={dayValue} onChange={(e) => setDayValue(e.target.value)} className="mt-1" />
            </div>
          )}
          {period === 'month' && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="mt-1" />
            </div>
          )}
          {period === 'range' && (
            <div>
              <Label className="text-xs">From — to (inclusive)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="month" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
                <span className="text-slate-400 text-xs">to</span>
                <Input type="month" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {[[2, 'Last 2 months'], [3, 'Last 3 months'], [6, 'Last 6 months']].map(([n, l]) => (
                  <button key={n} type="button" onClick={() => { setRangeFrom(shiftMonth(thisMonth(), -(n - 1))); setRangeTo(thisMonth()); }}
                    className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">{l}</button>
                ))}
              </div>
            </div>
          )}
          {period === 'months' && (
            <div>
              <Label className="text-xs">Months ({pickedMonths.length} picked)</Label>
              <div className="flex flex-wrap gap-1 mt-1 max-h-28 overflow-y-auto">
                {recentMonths().map(m => (
                  <button key={m} type="button" onClick={() => togglePicked(m)}
                    className={`text-[11px] px-2 py-0.5 rounded border ${pickedMonths.includes(m) ? 'bg-indigo-600 border-indigo-600 text-white font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {monthLabel(m)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {period === 'fy' && (
            <div>
              <Label className="text-xs">Financial Year</Label>
              <Select value={fyValue} onValueChange={setFyValue}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyOptions(6).map(fy => <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isPending && (<div>
            <Label className="text-xs">Include</Label>
            <div className="flex items-center gap-4 mt-1.5">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={includeOrders} onCheckedChange={setIncludeOrders} /> Orders
              </Label>
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={includeDispatches} onCheckedChange={setIncludeDispatches} /> Dispatches
              </Label>
            </div>
          </div>)}
          {!isPending && includeDispatches && canIncludeCN && (
            <div>
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={includeCreditNotes} onCheckedChange={setIncludeCreditNotes} />
                Include goods-return credit notes
              </Label>
              <p className="text-[11px] text-slate-400 mt-1">
                {includeCreditNotes
                  ? 'Returns appear as negative lines and reduce dispatch totals.'
                  : 'Off: clean sales only (returns excluded) — the usual customer copy.'}
              </p>
            </div>
          )}
          {(isWholesaler || retailers.length > 0) && (
            <div>
              <Label className="text-xs">For</Label>
              <Select value={endCustomer} onValueChange={setEndCustomer}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All orders (own shop + retailers)</SelectItem>
                  <SelectItem value="own">Own shop</SelectItem>
                  <SelectItem value="untagged">Not tagged</SelectItem>
                  {activeRetailers.map(r => (
                    <SelectItem key={r.retailer_id} value={r.retailer_id}>
                      {r.name}{r.alias ? <span className="text-slate-400"> · {r.alias}</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">
                Who the wholesaler placed the orders for. Dispatches follow their matched order lines; lines without an order copy cannot be attributed.
              </p>
            </div>
          )}
          {stores.length > 0 && (
            <div>
              <Label className="text-xs">Store</Label>
              <Select value={store} onValueChange={setStore}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stores</SelectItem>
                  <SelectItem value="head">SAP code itself (store not specified)</SelectItem>
                  {stores.filter(s => !s.is_head).map(s => (
                    <SelectItem key={s.store_id || s.ship_to_code} value={s.ship_to_code}>
                      {s.display_name || s.name || s.ship_to_code}{s.city ? <span className="text-slate-400"> · {s.city}</span> : null}
                    </SelectItem>
                  ))}
                  <SelectItem value="none">No store</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">
                Which ship-to location under the SAP code. Orders and dispatches carry their own ship-to; billing is not split.
              </p>
            </div>
          )}
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Preview: <span className="font-medium">{customer?.name || customer?.customer_code}</span> · {periodLabel} · {isPending ? 'Pending orders (open lines placed in this period)' : ([includeOrders && 'Orders', includeDispatches && 'Dispatches'].filter(Boolean).join(' + ') || '—')}
            {defaultDivision && defaultDivision !== 'all' && <> · {defaultDivision}</>}
            {endCustomerLabel && <> · For: <span className="font-medium">{endCustomerLabel}</span></>}
            {storeLabel && <> · Store: <span className="font-medium">{storeLabel}</span></>}
          </div>
        </div>
        )}
        </div>
        <DialogFooter className="gap-2 shrink-0 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!downloading}>{view ? 'Close' : 'Cancel'}</Button>
          {!view && (
            <Button variant="outline" className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
              disabled={!canSubmit || viewing || !!downloading} onClick={handleView}
              title="Show the report here — no download">
              {viewing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
              {viewing ? 'Loading…' : 'View'}
            </Button>
          )}
          <Button
            variant="outline"
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            disabled={!canSubmit || !!downloading}
            onClick={() => handleDownload('excel')}
          >
            <Download size={14} className="mr-1" />
            {downloading === 'excel' ? 'Building...' : 'Excel'}
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={!canSubmit || !!downloading}
            onClick={() => handleDownload('pdf')}
          >
            <Download size={14} className="mr-1" />
            {downloading === 'pdf' ? 'Building...' : 'PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
